import React from 'react';
import moment from 'moment';
import styles from './userCompensationsPageStyles.scss';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import CompensationCard from 'buyside/components/presentation/CompensationIndexPage/CompensationCard/compensationCard';
import DeleteDraftCompensationButtonContainer from 'buyside/components/container/UserAccount/DeleteDraftCompensationButtonContainer';
import Link from 'buyside/components/presentation/Link/link';

import { compensationNewUrl, compensationEditUrl } from 'buyside/util/urlHelper';

import { useCurrentUserAccountCompensationsQuery } from 'interfaces/graphql-typings';

import LoadingSpinner, {
  SIZES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';

const UserCompensationsPage = (): React.ReactElement => {
  const { loading, data } = useCurrentUserAccountCompensationsQuery();

  if (loading || !data?.currentUser) return <LoadingSpinner size={SIZES.LARGE} />;

  const { draftCompensations, isAdmin, publishedCompensations } = data.currentUser;

  const hasCompensation = !!draftCompensations?.length || !!publishedCompensations?.length;
  const actualCompDrafts = draftCompensations?.filter(draft => draft?.category === 'actual');

  return (
    <>
      <div className={styles.pageTitle}>
        Compensation
        <Button
          url={compensationNewUrl()}
          text="Submit Compensation"
          type={BUTTON_TYPES.MAJOR}
          size={BUTTON_SIZES.SMALL}
          className={styles.submitButton}
        />
      </div>
      <div className={styles.noContentMsg}>
        <p>
          Please{' '}
          <Link to={compensationNewUrl()} className={styles.highlightLink}>
            click here
          </Link>{' '}
          to start adding a <strong>new Compensation</strong>.
        </p>
        <Button url={compensationNewUrl()} text="Submit Compensation" type={BUTTON_TYPES.MAJOR} />
      </div>
      {hasCompensation ? (
        <div>
          {actualCompDrafts?.length ? (
            <div>
              <h4 className={styles.pageSubsectionTitle}>Draft</h4>
              <table className={styles.draftsTable}>
                <tbody>
                  {actualCompDrafts?.map(comp => (
                    <tr key={comp?.id}>
                      <td>
                        <Link to={compensationEditUrl(comp?.id!)}>
                          {`${comp?.year} actual - ${(comp?.year || 0) + 1} estimated`} compensation
                        </Link>
                      </td>
                      <td>{moment(comp?.updatedAt).fromNow()}</td>
                      <td>
                        <DeleteDraftCompensationButtonContainer actualCompensation={comp!} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {publishedCompensations?.length ? (
            <div>
              <h4 className={styles.pageSubsectionTitle}>Published</h4>
              {publishedCompensations.map(comp => (
                <div key={comp?.id} className={styles.cardContainer} data-testid="card-container">
                  <CompensationCard node={comp} isAdmin={isAdmin} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className={styles.noContentMsg}>
          <p>You haven't posted any compensation yet.</p>
          <Button
            url={compensationNewUrl()}
            text="Submit Compensation"
            type={BUTTON_TYPES.PRIMARY}
          />
        </div>
      )}
    </>
  );
};

export default UserCompensationsPage;
